import { inject } from '@angular/core';
import { HttpEvent, HttpHandlerFn, HttpRequest, HttpErrorResponse } from '@angular/common/http';

import { Observable, catchError, finalize, throwError } from 'rxjs';
import { Store } from '@ngxs/store';

// actions
import { SetLoading, SetHTTPErrors } from '@app/store/app/states/ui/ui.actions';

// config
import { appHTTPContext, skipLoadingSpinnerContext } from '@app/config/http-context.config';

// Models
import { UIStateModel } from '@app/models/ui.model';

// Services
import { GlobalHttpAbortService } from '@app/services/http-abort/http-abort.service';

const requestMap = new Map<string, string[]>();
const failedRequestsMap = new WeakMap<HttpRequest<unknown>, { error: HttpErrorResponse; requestId: string }>();
const failedRequests = new Set<HttpRequest<unknown>>();
const getActiveRequestId = (reqTag: string) =>
  `${reqTag}-${Date.now().toString(36) + Math.random().toString(36).substr(2)}`;

export const GlobalHttpInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const store = inject(Store);
  const abortService = inject(GlobalHttpAbortService);

  const requestTag = req.context.get(appHTTPContext);
  const shouldSkipLoadingSpinner = req.context.get(skipLoadingSpinnerContext);

  // Only tagged requests use this interceptor's global loading/error pipeline.
  // Untagged requests are passed through untouched.
  if (!requestTag) {
    return next(req);
  }

  const signal = abortService.getAbortSignal();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const abortableReq = (req as any).clone({ signal }) as HttpRequest<unknown>;

  const requestId = getActiveRequestId(requestTag);

  if (!shouldSkipLoadingSpinner) {
    // Track this request as in-flight so global loading can represent "any active tracked request".
    trackRequestStart(requestTag, requestId);
    store.dispatch(new SetLoading(true));
  }

  return next(abortableReq).pipe(
    finalize(() => {
      if (!shouldSkipLoadingSpinner) {
        clearTrackedRequest(requestTag, requestId);
      }

      // Flush errors when loading cycle is done, or immediately for bypassed requests
      // that captured an error and are not blocked by tracked requests.
      const shouldFlushErrors = shouldFlushErrorsForRequest(abortableReq, shouldSkipLoadingSpinner);

      if (shouldFlushErrors) {
        if (!shouldSkipLoadingSpinner) {
          // Turn global loading off only for tracked requests.
          store.dispatch(new SetLoading(false));
        }

        // Publish errors to UI state.
        publishErrors(store, abortableReq);
      }
    }),
    catchError((error) => {
      if (shouldCaptureError(abortableReq, error)) {
        // Capture HTTP error so finalize can publish it in a single UI update.
        captureFailedRequest(abortableReq, requestId, error);
      }

      return throwError(() => error);
    }),
  );
};

const trackRequestStart = (requestTag: string, requestId: string): void => {
  const inFlightRequests = requestMap.get(requestTag) ?? [];
  requestMap.set(requestTag, [...inFlightRequests, requestId]);
};

const clearTrackedRequest = (requestTag: string, requestId: string): void => {
  const inFlightRequests = requestMap.get(requestTag);
  requestMap.set(
    requestTag,
    (inFlightRequests ?? []).filter((rId) => rId !== requestId),
  );
};

const hasAnyTrackedRequestsInFlight = (): boolean =>
  Array.from(requestMap.values()).some((reqArray) => reqArray.length > 0);

const shouldFlushErrorsForRequest = (abortableReq: HttpRequest<unknown>, shouldSkipLoadingSpinner: boolean): boolean =>
  !hasAnyTrackedRequestsInFlight() && (!shouldSkipLoadingSpinner || failedRequestsMap.has(abortableReq));

const collectErrorsForRequest = (abortableReq: HttpRequest<unknown>): UIStateModel['httpErrors'] => {
  const storeErrors: UIStateModel['httpErrors'] = [];

  failedRequests.forEach(() => {
    if (!failedRequestsMap.has(abortableReq)) {
      return;
    }

    const { error } = failedRequestsMap.get(abortableReq)!;
    storeErrors.push({
      error,
      originalRequest: abortableReq,
      message: 'Server error. Please try again.',
    });

    failedRequestsMap.delete(abortableReq);
  });

  return storeErrors;
};

const shouldCaptureError = (request: HttpRequest<unknown>, error: unknown): error is HttpErrorResponse =>
  request.headers.get('X-skip-interceptor-error-handling') === 'false' && error instanceof HttpErrorResponse;

const captureFailedRequest = (request: HttpRequest<unknown>, requestId: string, error: HttpErrorResponse): void => {
  failedRequests.add(request);
  failedRequestsMap.set(request, { error, requestId });
};

const publishErrors = (store: Store, abortableReq: HttpRequest<unknown>): void => {
  const storeErrors = collectErrorsForRequest(abortableReq);
  store.dispatch(new SetHTTPErrors(storeErrors));
};
